// Lead form: name, email, phone, dates, party size, wedding?, inquiry notes,
// callback_pref -> leads/{id}. After submit, shows thank-you (no reservation
// in this flow - Julie sends a quote with a signed link to return).
import { useState } from "preact/hooks";
import { CALLBACK_PREFS, CALLBACK_LABELS, WEDDING_INFO_TEXT, PAYMENT_CHANNELS, PAYMENT_CHANNEL_LABELS, DEPOSIT_NOTE_TEXT } from "../config.js";
import { validateLead } from "../lib/validation.js";
import { submitLead } from "../lib/firestore.js";
import { describeError, reportError } from "../lib/errlog.js";
import { estimatePrice } from "../lib/availability.js";
import { TextField, SelectField, MissingBanner } from "./Fields.jsx";
import { SmsConsent } from "./SmsConsent.jsx";
import { Status } from "./Status.jsx";

// Friendly names for every validatable field (the MissingBanner speaks human,
// not field keys). `nights` is excluded — it renders as its own warning band
// (a constraint, not a missing item).
const MISSING_LABELS = {
  first_name: "your first name",
  last_name: "your last name",
  email: "a valid email",
  phone: "a valid phone number",
  checkin_date: "a check-in date",
  checkout_date: "a check-out date",
  adults: "adults",
  children: "children",
  wedding: "wedding yes/no",
  payment_channel: "a payment method",
  callback_pref: "how Julie should reach you",
  sms_consent: "consent to booking texts",
};

export function LeadForm({ initialDates, pricingBand, rateBands, onDone }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    checkin_date: initialDates?.checkin || "",
    checkout_date: initialDates?.checkout || "",
    adults: "2",
    children: "0",
    wedding: "no",
    inquiry_notes: "",
    callback_pref: "call_me",
    // Session 49 (user): check is the default payment method — the old blank
    // start + "no deposit" card label steered everyone to card.
    payment_channel: "check",
    sms_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    // Clear the field's error as soon as the guest fixes it.
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  }

  // Dynamic price estimate for the selected dates (shown under the dates, after
  // the guest has picked them on the calendar). Recomputes when dates change.
  const est = estimatePrice(
    { pricing_band: pricingBand, rate_bands: rateBands },
    form.checkin_date,
    form.checkout_date
  );

  async function submit(e) {
    e.preventDefault();
    const { errors: e2, phone } = validateLead(form);
    setErrors(e2);
    if (Object.keys(e2).length > 0) return;
    setBusy(true);
    setStatus(null);
    try {
      const res = await submitLead({ ...form, phone_e164: phone });
      onDone?.({ id: res.id, form, nights: res.nights });
    } catch (err) {
      reportError("lead_submit", err);
      setStatus({ kind: "err", text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="surface" onSubmit={submit} noValidate>
      <h2>Request your stay</h2>
      <div class="row">
        <TextField label="First name" field="first_name" value={form.first_name} onInput={set} error={errors.first_name} required />
        <TextField label="Last name" field="last_name" value={form.last_name} onInput={set} error={errors.last_name} required />
      </div>
      <TextField label="Email" field="email" type="email" value={form.email} onInput={set} error={errors.email} required />
      <TextField label="Phone" field="phone" type="tel" value={form.phone} onInput={set} error={errors.phone} placeholder="+1 555 867 5309" required />
      <div class="row">
        <TextField label="Check-in" field="checkin_date" type="date" value={form.checkin_date} onInput={set} error={errors.checkin_date} required />
        <TextField label="Check-out" field="checkout_date" type="date" value={form.checkout_date} onInput={set} error={errors.checkout_date} required />
      </div>
      {errors.nights ? <p class="band warn">{errors.nights}</p> : null}
      {est && !errors.nights ? (
        <p class="band price-estimate">
          Around ${Math.round((est.total / 100) * 100).toLocaleString("en-US")} for {est.nights} nights
          <span class="price-detail"> · ~${Math.round((est.perNight / 100) * 100).toLocaleString("en-US")}/night, before fees</span>
        </p>
      ) : null}
      <div class="row">
        <TextField label="Adults" field="adults" type="number" value={form.adults} onInput={set} error={errors.adults} required />
        <TextField label="Children" field="children" type="number" value={form.children} onInput={set} error={errors.children} required />
      </div>
      <SelectField
        label="Wedding?"
        field="wedding"
        value={form.wedding}
        onChange={set}
        options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]}
        required
      />
      {form.wedding === "yes" ? <p class="wedding-info">{WEDDING_INFO_TEXT}</p> : null}
      <label class="field">
        <span class="field-label">Anything else?</span>
        <textarea
          value={form.inquiry_notes}
          onInput={(e) => set("inquiry_notes", e.currentTarget.value)}
          rows={3}
          placeholder="Questions, special requests, arrival plans..."
        />
      </label>
      <SelectField
        label="Payment method"
        field="payment_channel"
        value={form.payment_channel}
        onChange={set}
        error={errors.payment_channel}
        options={PAYMENT_CHANNELS.map((c) => ({ value: c, label: PAYMENT_CHANNEL_LABELS[c] }))}
        placeholder="Select…"
        required
      />
      <p class="hint">{DEPOSIT_NOTE_TEXT}</p>
      <SelectField
        label="How should Julie reach you?"
        field="callback_pref"
        value={form.callback_pref}
        onChange={set}
        options={CALLBACK_PREFS.map((p) => ({ value: p, label: CALLBACK_LABELS[p] }))}
        required
      />
      <SmsConsent
        value={form.sms_consent}
        onChange={(v) => set("sms_consent", v)}
        error={errors.sms_consent}
      />
      <MissingBanner errors={Object.fromEntries(Object.entries(errors).filter(([k]) => k !== "nights"))} labels={MISSING_LABELS} />
      <button type="submit" disabled={busy}>{busy ? "Sending..." : "Send request"}</button>
      <Status kind={status?.kind === "err" ? "err" : "ok"}>{status?.text}</Status>
    </form>
  );
}
